import { useEffect, useState } from "react";
import {
  ApiError,
  addPasskey,
  api,
  defaultDeviceName,
  readPlatformEnv,
  shouldOfferPasskey,
  webauthnErrorMessage,
  webauthnSupported,
  type Me,
} from "@helm/core";

const DISMISSED_KEY = "helm:passkey-offer-dismissed";

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function dismiss(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    /* private mode: the offer comes back next time, which is harmless */
  }
}

/**
 * After a PIN sign-in (ADR-0035), offer "Create a passkey on this device" so later sign-ins use
 * Face ID / Touch ID / fingerprint. Shown only behind the device gate (the device is approved),
 * where passkeys work at this address, and until created or dismissed on this device.
 */
export function PasskeyOffer({ me }: { me: Me }): React.JSX.Element | null {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (me.auth_method !== "pin") return;
    let live = true;
    api
      .meta()
      .then((m) => {
        if (!live) return;
        setShow(
          shouldOfferPasskey({
            authMethod: me.auth_method,
            passkeysHere: (m.passkeys_here ?? m.passkeys) === true,
            supported: webauthnSupported(),
            dismissed: dismissed(),
          }),
        );
      })
      .catch(() => {
        /* no offer */
      });
    return () => {
      live = false;
    };
  }, [me.auth_method]);

  if (!show) return null;
  const create = (): void => {
    setBusy(true);
    setError(null);
    addPasskey(defaultDeviceName(readPlatformEnv()).slice(0, 60))
      .then(() => {
        setDone(true);
        dismiss();
        setTimeout(() => {
          setShow(false);
        }, 5000);
      })
      .catch((e: unknown) => {
        setError(
          e instanceof ApiError
            ? e.code === "DEVICE_NOT_APPROVED"
              ? "Approve this device from another one first (Settings → Devices), then try again."
              : e.message
            : webauthnErrorMessage(e),
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div
      className="pin-offer"
      role="dialog"
      aria-label="Create a passkey on this device"
      data-testid="passkey-offer"
    >
      {done ? (
        <div role="status">Passkey created. Next time, choose “Sign in with passkey”.</div>
      ) : (
        <>
          <strong>Create a passkey on this device?</strong>
          <div>
            You signed in with your PIN. With a passkey, next time you sign in with Face ID, Touch ID or your
            fingerprint instead, and you can change security settings from here.
          </div>
          {error && (
            <div className="login-error" role="alert">
              {error}
            </div>
          )}
          <div className="pin-offer-actions">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => {
                dismiss();
                setShow(false);
              }}
            >
              Not now
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={create}>
              {busy ? "Waiting for your device…" : "Create a passkey"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
